"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readQuantity } from '@/lib/tools/numeric-input'

const FALLBACK_RELATED = [
  "energy-cost-calculator",
  "refrigerator-size-calculator",
  "refrigerator-lifespan-calculator",
  "kitchen-space-planner",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~10 sec",
    })
  }
  return out
}

// Conversion factors to milliliters (base unit) — preserved verbatim.
const CONVERSIONS: Record<string, number> = {
  teaspoon: 4.92892,
  tablespoon: 14.7868,
  "fluid-ounce": 29.5735,
  cup: 236.588,
  pint: 473.176,
  quart: 946.353,
  gallon: 3785.41,
  milliliter: 1,
  liter: 1000,
  gram: 1, // 1:1 baseline; density applies in volume↔weight conversions
  ounce: 28.3495,
  pound: 453.592,
}

const VOLUME_UNITS = ["teaspoon", "tablespoon", "fluid-ounce", "cup", "pint", "quart", "gallon", "milliliter", "liter"]
const WEIGHT_UNITS = ["gram", "ounce", "pound"]

const DENSITIES: Record<string, number> = {
  water: 1.0,
  flour: 0.53,
  sugar: 0.85,
  butter: 0.91,
  oil: 0.92,
  milk: 1.03,
  honey: 1.42,
}

export function RecipeMeasurementConverterPageClient() {
  const meta = TOOL_METADATA["recipe-measurement-converter"]

  const [amount, setAmount] = useState("1")
  const [fromUnit, setFromUnit] = useState("cup")
  const [toUnit, setToUnit] = useState("gram")
  const [ingredient, setIngredient] = useState("water")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    const value = readQuantity(amount, { min: 0.001, max: 10000 })
    if (value === null) return null

    const density = DENSITIES[ingredient] ?? 1.0
    const inML = value * CONVERSIONS[fromUnit]

    const isFromVolume = VOLUME_UNITS.includes(fromUnit)
    const isToWeight = WEIGHT_UNITS.includes(toUnit)
    const isFromWeight = WEIGHT_UNITS.includes(fromUnit)
    const isToVolume = VOLUME_UNITS.includes(toUnit)

    let baseValue = inML
    if (isFromVolume && isToWeight) baseValue = inML * density
    else if (isFromWeight && isToVolume) baseValue = inML / density

    const converted = baseValue / CONVERSIONS[toUnit]
    return { converted, fromAmount: value }
  }, [amount, fromUnit, toUnit, ingredient])

  return (
    <ToolPageShell
      toolName={meta.name}
      heroEyebrow={meta.category}
      heroTitlePre="Convert any cup, gram, or pound to"
      heroTitleItalic="anything"
      heroTitlePost="else."
      heroSubtitle={meta.mediumDescription}
      heroMeta={["⏱ ~5 sec", "USDA densities"]}
      heroShare={<ToolShareBar title={meta.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "120g", label: "1 cup AP flour", note: "USDA" },
            { value: "200g", label: "1 cup sugar", note: "Granulated" },
            { value: "227g", label: "1 cup butter", note: "Solid" },
            { value: "236ml", label: "1 US cup", note: "Volume" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={meta.faqs.slice(0, 6).map((f) => ({ question: f.question, answer: f.answer }))}
      relatedTools={buildRelated(meta.relatedTools, "recipe-measurement-converter")}
      cta={{
        title: "Precision in. Precision out. Cold storage too.",
        body: "Browse French door and counter-depth fridges with humidity-controlled drawers built for serious home bakers.",
        buttonLabel: "Browse French door fridges",
        buttonHref: "/collections/french-door-refrigerators",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Volume to weight,
                <br />
                weight to volume.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Pick the ingredient — density makes flour 47% lighter than water for the same cup.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                    style={{ fontVariantNumeric: "tabular-nums" }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                      From
                    </label>
                    <select
                      value={fromUnit}
                      onChange={(e) => setFromUnit(e.target.value)}
                      className="w-full px-3 py-4 text-[15px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                    >
                      <option value="teaspoon">tsp</option>
                      <option value="tablespoon">tbsp</option>
                      <option value="fluid-ounce">fl oz</option>
                      <option value="cup">cup</option>
                      <option value="pint">pint</option>
                      <option value="quart">quart</option>
                      <option value="gallon">gallon</option>
                      <option value="milliliter">ml</option>
                      <option value="liter">L</option>
                      <option value="gram">g</option>
                      <option value="ounce">oz</option>
                      <option value="pound">lb</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                      To
                    </label>
                    <select
                      value={toUnit}
                      onChange={(e) => setToUnit(e.target.value)}
                      className="w-full px-3 py-4 text-[15px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                    >
                      <option value="teaspoon">tsp</option>
                      <option value="tablespoon">tbsp</option>
                      <option value="fluid-ounce">fl oz</option>
                      <option value="cup">cup</option>
                      <option value="pint">pint</option>
                      <option value="quart">quart</option>
                      <option value="gallon">gallon</option>
                      <option value="milliliter">ml</option>
                      <option value="liter">L</option>
                      <option value="gram">g</option>
                      <option value="ounce">oz</option>
                      <option value="pound">lb</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Ingredient (volume ↔ weight only)
                  </label>
                  <select
                    value={ingredient}
                    onChange={(e) => setIngredient(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[15px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="water">Water / liquids</option>
                    <option value="flour">All-purpose flour</option>
                    <option value="sugar">Granulated sugar</option>
                    <option value="butter">Butter</option>
                    <option value="oil">Cooking oil</option>
                    <option value="milk">Milk</option>
                    <option value="honey">Honey</option>
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Ignored when converting volume↔volume or weight↔weight.
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }}
                />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Converted
                  </div>
                  <div
                    className="font-black"
                    style={{ fontSize: "clamp(48px, 6.5vw, 80px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}
                  >
                    {result?.converted.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? "—"}
                    <span className="text-[18px] md:text-[22px] font-black tracking-tighter text-white/60 ml-2">{toUnit}</span>
                  </div>
                  <div className="mt-4 text-[15px] text-white/85 leading-[1.5]">
                    {result
                      ? `${result.fromAmount} ${fromUnit} of ${ingredient} = ${result.converted.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${toUnit}`
                      : "Enter an amount and pick units."}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick reference */}
          <div className="bg-white rounded-xl p-5 md:p-6 border border-gray-200">
            <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
              <h3 className="text-[16px] font-black tracking-[-0.02em] text-gray-900">
                Common kitchen conversions
              </h3>
              <span className="text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold">
                Standard US
              </span>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-[13px] text-gray-700">
              <div className="border-b border-gray-100 pb-1.5">1 cup = 16 tablespoons</div>
              <div className="border-b border-gray-100 pb-1.5">1 tablespoon = 3 teaspoons</div>
              <div className="border-b border-gray-100 pb-1.5">1 cup AP flour ≈ 120 g</div>
              <div className="border-b border-gray-100 pb-1.5">1 cup granulated sugar ≈ 200 g</div>
              <div className="border-b border-gray-100 pb-1.5">1 cup butter ≈ 227 g</div>
              <div className="border-b border-gray-100 pb-1.5">1 cup water = 236 ml</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · The right cold storage"
          title="French door fridges for serious bakers"
          description="Humidity-controlled drawers, dedicated produce zones, and dual cooling for laminated doughs."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                <em className="italic text-[#ff9951]">USDA</em> densities. Real ml.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Volume conversions are exact. Volume↔weight uses ingredient-specific density factors derived from USDA reference data and culinary standards.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Convert to milliliters",
                  d: "Every volume unit goes through ml as a base. 1 cup = 236.59 ml; 1 tbsp = 14.79 ml.",
                },
                {
                  n: "02",
                  t: "Apply density (if needed)",
                  d: "For volume↔weight, multiply by ingredient density (flour 0.53, sugar 0.85, butter 0.91, water 1.0, honey 1.42).",
                },
                {
                  n: "03",
                  t: "Convert to target unit",
                  d: "Final value divided by the target unit's ml-equivalent. 1 oz = 28.35 ml-density-equivalent at water's density.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Counter-depth fridges for compact kitchens"
          description="Built-in look without losing baking-station storage. 24–27 inch depth, generous fresh capacity."
          collectionType="counter-depth"
          fallbackCollectionType="french-door"
        />
      </section>
    </ToolPageShell>
  )
}
